import * as THREE from 'three';
import { createCameraLabel } from '../cameraProfile';
import { unprojectCameraPixel } from '../depth/cameraModels';
import { FRUSTUM_NAME, IMAGE_PLANE_NAME } from './cameraFrustum';
import { SCENE_UP_Z } from '../cameraOrientation';
import { initTiffWasm } from '../depth/readers/tiffWasm';
import { registerCameraEntry } from '../state/fileEntryState';
import { applyStonexColorCorrectionToPoints, applyStonexColorCorrectionToPreview, computeStonexFrameMultipliers, normalizeStonexColorCorrection, } from './stonexColorCorrection';
const FRUSTUM_DEPTH_METRES = 5;
// Segments per image axis for the distorted preview. The X300's k1 is around
// -0.485, so the corner rays leave the pinhole model by ~200 px; 16 segments
// keep the residual between grid vertices well under a pixel.
const IMAGE_GRID_SEGMENTS = 16;
function modelToViewer(point) {
    return new THREE.Vector3(point.y, point.x, point.z);
}
function rowMajorMatrix(values) {
    const matrix = new THREE.Matrix4();
    matrix.set(values[0], values[1], values[2], values[3], values[4], values[5], values[6], values[7], values[8], values[9], values[10], values[11], values[12], values[13], values[14], values[15]);
    return matrix;
}
function cameraToViewer(frame) {
    const pan = new THREE.Matrix4().makeRotationZ(frame.panDegrees * (Math.PI / 180));
    // Projection uses cameraPoint = Model2Camera * Pan * modelPoint.
    return rowMajorMatrix(frame.modelToCamera).multiply(pan).invert();
}
function transformCameraPoint(cameraPoint, cameraToModel) {
    return modelToViewer(cameraPoint.clone().applyMatrix4(cameraToModel));
}
function createScannerMarker() {
    const marker = new THREE.Group();
    marker.name = 'stonexScannerMarker';
    const sphere = new THREE.Mesh(new THREE.SphereGeometry(0.3, 16, 10), new THREE.MeshBasicMaterial({ color: 0xffd54f }));
    marker.add(sphere);
    // Model axes after the viewer's X/Y display swap: X->viewer Y,
    // Y->viewer X, Z->viewer Z.
    const positions = new Float32Array([0, 0, 0, 0, 2, 0, 0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 2]);
    const colors = new Float32Array([
        1, 0.2, 0.2, 1, 0.2, 0.2, 0.2, 1, 0.2, 0.2, 1, 0.2, 0.2, 0.4, 1, 0.2, 0.4, 1,
    ]);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    marker.add(new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ vertexColors: true })));
    return marker;
}
function pinholePoint(frame, u, v, depth) {
    return new THREE.Vector3(((u - frame.cx) / frame.fx) * depth, ((v - frame.cy) / frame.fy) * depth, depth);
}
/**
 * Camera-space position of image pixel (u, v) on the plane z = depth.
 *
 * The distorted variant unprojects through the same OpenCV model the point
 * colouring uses, so the preview covers the pixels the colours actually came
 * from. Rays stay on the constant-z plane: the surface is still flat, only its
 * outline and texture mapping bend.
 */
function imagePoint(frame, u, v, depth, distorted) {
    if (!distorted) {
        return pinholePoint(frame, u, v, depth);
    }
    const result = unprojectCameraPixel({
        cameraModel: 'pinhole-opencv',
        fx: frame.fx,
        fy: frame.fy,
        cx: frame.cx,
        cy: frame.cy,
        coefficients: frame.distortionCoefficients,
    }, [u, v]);
    const [x, y, z] = result.value;
    if (!result.valid || !result.converged || !(z > 1e-6)) {
        // Strong polynomials can fail to invert near the corners; the pinhole ray
        // keeps the mesh well-formed instead of folding it through the origin.
        return pinholePoint(frame, u, v, depth);
    }
    const scale = depth / z;
    return new THREE.Vector3(x * scale, y * scale, depth);
}
function imageGrid(frame, depth, distorted) {
    const segments = distorted ? IMAGE_GRID_SEGMENTS : 1;
    const points = [];
    const uvs = [];
    for (let row = 0; row <= segments; row++) {
        const t = row / segments;
        for (let column = 0; column <= segments; column++) {
            const s = column / segments;
            points.push(imagePoint(frame, s * frame.imageWidth, t * frame.imageHeight, depth, distorted));
            uvs.push(s, t);
        }
    }
    const triangles = [];
    const index = (row, column) => row * (segments + 1) + column;
    for (let row = 0; row < segments; row++) {
        for (let column = 0; column < segments; column++) {
            const a = index(row, column);
            const b = index(row, column + 1);
            const c = index(row + 1, column + 1);
            const d = index(row + 1, column);
            triangles.push(a, b, c, a, c, d);
        }
    }
    const border = [];
    for (let column = 0; column <= segments; column++) {
        border.push(index(0, column));
    }
    for (let row = 1; row <= segments; row++) {
        border.push(index(row, segments));
    }
    for (let column = segments - 1; column >= 0; column--) {
        border.push(index(segments, column));
    }
    for (let row = segments - 1; row >= 0; row--) {
        border.push(index(row, 0));
    }
    border.push(border[0]);
    return {
        points,
        uvs,
        triangles,
        border,
        corners: [index(0, 0), index(0, segments), index(segments, segments), index(segments, 0)],
    };
}
function createFrameGeometries(frame, distorted) {
    const cameraToModel = cameraToViewer(frame);
    const origin = transformCameraPoint(new THREE.Vector3(), cameraToModel);
    const grid = imageGrid(frame, FRUSTUM_DEPTH_METRES, distorted);
    const vertices = grid.points.map(point => transformCameraPoint(point, cameraToModel).sub(origin));
    const plane = new THREE.BufferGeometry();
    plane.setAttribute('position', new THREE.Float32BufferAttribute(vertices.flatMap(vertex => vertex.toArray()), 3));
    // DataTexture row zero is the image's top row, so map it to the top edge.
    plane.setAttribute('uv', new THREE.Float32BufferAttribute(grid.uvs, 2));
    plane.setIndex(grid.triangles);
    const linePoints = [];
    for (const corner of grid.corners) {
        linePoints.push(new THREE.Vector3(), vertices[corner]);
    }
    for (let i = 0; i + 1 < grid.border.length; i++) {
        linePoints.push(vertices[grid.border[i]], vertices[grid.border[i + 1]]);
    }
    const frustum = new THREE.BufferGeometry().setFromPoints(linePoints);
    return { plane, frustum };
}
/**
 * Look-through basis for a frame.
 *
 * modelToViewer swaps X and Y, which is a reflection, so the frame's rotation
 * matrix cannot be handed to the viewer camera as-is. Transforming points and
 * deriving the basis from them keeps the result consistent with the geometry
 * that is actually drawn, whatever the handedness.
 */
function frameView(frame, cameraToModel, origin) {
    const atPixel = (u, v) => transformCameraPoint(pinholePoint(frame, u, v, FRUSTUM_DEPTH_METRES), cameraToModel).sub(origin);
    const topCentre = atPixel(frame.imageWidth / 2, 0);
    const bottomCentre = atPixel(frame.imageWidth / 2, frame.imageHeight);
    return {
        forward: atPixel(frame.imageWidth / 2, frame.imageHeight / 2),
        up: topCentre.sub(bottomCentre).normalize(),
        fovYDegrees: 2 * Math.atan(frame.imageHeight / (2 * frame.fy)) * (180 / Math.PI),
        // X3A archives measure their vertical angle about Z, so upright is +Z and
        // the roll can be snapped to it.
        rollSnapAxis: SCENE_UP_Z,
        corners: [
            atPixel(0, 0),
            atPixel(frame.imageWidth, 0),
            atPixel(frame.imageWidth, frame.imageHeight),
            atPixel(0, frame.imageHeight),
        ],
    };
}
function frameDetails(frame) {
    const coefficients = frame.distortionCoefficients;
    // OpenCV pads to a fixed layout; trailing zeros carry no information.
    const lastSet = coefficients.reduce((last, value, i) => (value !== 0 ? i : last), -1);
    return [
        { label: 'Family', value: frame.type === 'U' ? 'U (upper)' : 'D (lower)' },
        { label: 'Pan', value: `${frame.panDegrees.toFixed(3)}°` },
        { label: 'Image', value: `${frame.imageWidth} x ${frame.imageHeight} px` },
        { label: 'Focal', value: `fx ${frame.fx.toFixed(2)}, fy ${frame.fy.toFixed(2)} px` },
        { label: 'Principal', value: `cx ${frame.cx.toFixed(2)}, cy ${frame.cy.toFixed(2)} px` },
        {
            label: 'Distortion',
            value: coefficients
                .slice(0, Math.max(lastSet + 1, 5))
                .map(value => value.toFixed(6))
                .join(', '),
        },
    ];
}
function createFrameVisualization(frame, frameNumber, multipliers, settings, station) {
    const cameraToModel = cameraToViewer(frame);
    const origin = transformCameraPoint(new THREE.Vector3(), cameraToModel);
    const geometries = createFrameGeometries(frame, false);
    const group = new THREE.Group();
    group.name = `camera_${frame.name}`;
    // The frustum geometry is built in the station's own axes about `origin`, so
    // the station's placement rides on the group: its rotation as the group's
    // orientation, its translation folded into the position.
    group.stationLocalOrigin = { x: origin.x, y: origin.y, z: origin.z };
    placeFrameGroup(group, station);
    group.userData.frame = frame;
    group.userData.geometries = { pinhole: geometries };
    group.userData.view = frameView(frame, cameraToModel, origin);
    group.userData.frameDetails = frameDetails(frame);
    const helper = new THREE.LineSegments(geometries.frustum, new THREE.LineBasicMaterial({ color: frame.type === 'U' ? 0x42a5f5 : 0xffa726 }));
    helper.name = FRUSTUM_NAME;
    group.add(helper);
    // previewRgba is raw sensor data; the texture holds the corrected copy so
    // switching modes only rewrites this buffer.
    const rawRgba = frame.previewRgba instanceof Uint8Array
        ? frame.previewRgba
        : new Uint8Array(frame.previewRgba);
    const rgba = new Uint8Array(rawRgba.length);
    applyStonexColorCorrectionToPreview(rawRgba, multipliers, frameNumber, settings, rgba);
    const texture = new THREE.DataTexture(rgba, frame.previewWidth, frame.previewHeight, THREE.RGBAFormat, THREE.UnsignedByteType);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    group.userData.frameNumber = frameNumber;
    group.userData.rawPreviewRgba = rawRgba;
    group.userData.previewTexture = texture;
    const plane = new THREE.Mesh(geometries.plane, new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        // Camera previews are reference imagery. Keep their decoded sRGB values
        // independent of the renderer exposure used by the geometry brightness control.
        toneMapped: false,
        transparent: true,
        opacity: 0.88,
        depthWrite: false,
    }));
    plane.name = IMAGE_PLANE_NAME;
    plane.visible = false;
    plane.renderOrder = 2;
    group.add(plane);
    const label = createCameraLabel(frame.name.replace(/\.x3i$/i, ''));
    label.name = 'cameraLabel';
    label.visible = false;
    group.add(label);
    return group;
}
export function addStonexCameraVisualization(host, data) {
    const frames = data.metadata?.stonexCameraFrames;
    if (!frames?.length) {
        return false;
    }
    const profileName = data.metadata?.stonexCameraProfileName || data.fileName || 'X3A';
    const profile = new THREE.Group();
    profile.name = `stonex_cameras_${profileName}`;
    profile.userData.hasImagePlanes = true;
    profile.userData.supportsDistortionToggle = true;
    profile.userData.hasScannerMarker = true;
    profile.userData.excludeFromFit = true;
    profile.userData.cameraCount = frames.length;
    profile.userData.imagesVisible = false;
    // Distortion is the physically correct footprint, so it is the default; the
    // geometry is built on the first setStonexImageDistortion call.
    profile.userData.imagesDistorted = true;
    profile.userData.imageDistortionAvailable = true;
    profile.userData.scannerVisible = true;
    const calibration = data.metadata?.stonexColorCalibration;
    const settings = normalizeStonexColorCorrection(data.metadata?.stonexColorCorrection);
    profile.userData.colorCalibration = calibration ?? null;
    profile.userData.colorCorrection = settings;
    profile.userData.colorCorrectionAvailable = !!calibration;
    profile.userData.profileName = profileName;
    const multipliers = calibration
        ? computeStonexFrameMultipliers(calibration, settings)
        : new Float32Array(frames.length * 3).fill(1);
    // One scanner marker per station, at the station's own origin, rather than a
    // single one at the world origin that would claim every panorama was shot
    // from the same tripod.
    const stationOf = stationTransformLookup(host);
    const markedStations = new Set();
    frames.forEach((frame, frameNumber) => {
        const station = stationOf(frame.scanStem);
        if (!markedStations.has(frame.scanStem ?? '')) {
            markedStations.add(frame.scanStem ?? '');
            const marker = createScannerMarker();
            marker.userData.stationStem = frame.scanStem ?? '';
            if (station) {
                marker.position.setFromMatrixPosition(station);
                marker.quaternion.setFromRotationMatrix(station);
            }
            profile.add(marker);
        }
        profile.add(createFrameVisualization(frame, frameNumber, multipliers, settings, station));
    });
    host.scene.add(profile);
    host.cameraGroups.push(profile);
    host.cameraNames.push(`${profileName} cameras`);
    registerCameraEntry(host);
    return true;
}
/** Places one frame group from its station-local origin and station pose. */
function placeFrameGroup(group, station) {
    const local = group.stationLocalOrigin;
    const position = new THREE.Vector3(local.x, local.y, local.z);
    if (station) {
        position.applyMatrix4(station);
        group.quaternion.setFromRotationMatrix(station);
    }
    else {
        group.quaternion.identity();
    }
    group.position.copy(position);
    // The coordinate label reads this, and what a user wants to see there is
    // where the camera actually stood, not where it sat before registration.
    group.originalPosition = { x: position.x, y: position.y, z: position.z };
}
/**
 * Looks a station's current placement up from the scan that belongs to it.
 *
 * Deliberately read from the viewer rather than from anything the parser
 * emitted: cameras then follow whatever alignment is on screen, whether it came
 * from "align all", the archive pipeline, or a matrix typed in by hand.
 */
function stationTransformLookup(host) {
    const byStem = new Map();
    for (let index = 0; index < host.spatialFiles.length; index++) {
        const name = host.spatialFiles[index]?.metadata?.embeddedScanName;
        if (name) {
            byStem.set(name.replace(/\.x3r$/i, ''), host.transformationMatrices[index]);
        }
    }
    return stem => (stem ? (byStem.get(stem) ?? null) : null);
}
/**
 * Re-places every panorama on its own station after the scans have moved.
 *
 * Called when registration changes a transform; without it the frustums stay
 * where they were built, which for an unregistered archive means every station
 * piled on the origin.
 */
export function updateStonexCameraStations(host) {
    const stationOf = stationTransformLookup(host);
    for (const profile of host.cameraGroups) {
        for (const child of profile.children) {
            if (child.name === 'stonexScannerMarker') {
                const station = stationOf(child.userData.stationStem);
                if (station) {
                    child.position.setFromMatrixPosition(station);
                    child.quaternion.setFromRotationMatrix(station);
                }
                continue;
            }
            if (!(child instanceof THREE.Group) || !child.name.startsWith('camera_')) {
                continue;
            }
            const frame = child.userData.frame;
            placeFrameGroup(child, stationOf(frame?.scanStem));
        }
    }
}
export function setStonexImagesVisible(group, visible) {
    group.userData.imagesVisible = visible;
    group.traverse(object => {
        if (object.name === IMAGE_PLANE_NAME) {
            object.visible = visible;
        }
    });
}
/**
 * Swaps every frame in the profile between the pinhole quad and the mesh
 * unprojected through the calibrated distortion.
 *
 * The distorted geometry needs the Rust/WASM camera kernel, which the webview
 * only initialises on demand, so this is async and caches per frame group.
 * Resolves to the mode actually applied — callers should re-render afterwards
 * and read `userData.imageDistortionAvailable` to reflect a failed kernel.
 */
export async function setStonexImageDistortion(group, distorted) {
    var _a;
    if (distorted && !(await initTiffWasm())) {
        group.userData.imageDistortionAvailable = false;
        group.userData.imagesDistorted = false;
        return false;
    }
    group.userData.imageDistortionAvailable = true;
    for (const child of group.children) {
        const frame = child.userData?.frame;
        if (!frame) {
            continue;
        }
        const cache = ((_a = child.userData).geometries ?? (_a.geometries = {}));
        const key = distorted ? 'distorted' : 'pinhole';
        const geometries = (cache[key] ?? (cache[key] = createFrameGeometries(frame, distorted)));
        const plane = child.getObjectByName(IMAGE_PLANE_NAME);
        const frustum = child.getObjectByName(FRUSTUM_NAME);
        if (plane) {
            plane.geometry = geometries.plane;
        }
        if (frustum) {
            frustum.geometry = geometries.frustum;
        }
    }
    group.userData.imagesDistorted = distorted;
    return distorted;
}
/**
 * Re-derives photographic colour for a Stonex profile: the preview thumbnails
 * on the image planes and every point cloud decoded from the same archive.
 *
 * This is a pure channel multiply over the raw arrays the parser retained, so
 * it costs one O(points) pass instead of the ~5 s a re-parse would take. Point
 * colours are mutated in place because `buildOriginalColorArray` shares the
 * parser's `colorsArray` with the GPU attribute zero-copy.
 */
export function setStonexColorCorrection(host, group, settings) {
    const calibration = group.userData.colorCalibration;
    if (!calibration) {
        return;
    }
    const resolved = normalizeStonexColorCorrection(settings);
    group.userData.colorCorrection = resolved;
    const multipliers = computeStonexFrameMultipliers(calibration, resolved);
    for (const child of group.children) {
        const rawRgba = child.userData?.rawPreviewRgba;
        const texture = child.userData?.previewTexture;
        if (!rawRgba || !texture) {
            continue;
        }
        applyStonexColorCorrectionToPreview(rawRgba, multipliers, child.userData.frameNumber, resolved, texture.image.data);
        texture.needsUpdate = true;
    }
    const profileName = group.userData.profileName;
    for (let index = 0; index < host.spatialFiles.length; index++) {
        const data = host.spatialFiles[index];
        if (data?.metadata?.stonexCameraProfileName !== profileName) {
            continue;
        }
        const rawColors = data.metadata?.stonexRawColors;
        const frameIndices = data.metadata?.stonexFrameIndices;
        if (!rawColors || !frameIndices || !data.colorsArray) {
            continue;
        }
        applyStonexColorCorrectionToPoints(rawColors, frameIndices, multipliers, resolved, data.colorsArray);
        (data.metadata ?? (data.metadata = {})).stonexColorCorrection = resolved;
        // Only flag the attribute when it is the shared colorsArray; other colour
        // modes rebuild from it when the user switches back to "original".
        const geometry = host.meshes[index]?.geometry;
        const attribute = geometry?.getAttribute('color');
        if (attribute && attribute.array === data.colorsArray) {
            attribute.needsUpdate = true;
        }
    }
}
export function setStonexScannerVisible(group, visible) {
    group.userData.scannerVisible = visible;
    const marker = group.getObjectByName('stonexScannerMarker');
    if (marker) {
        marker.visible = visible;
    }
}
